import { useEffect, useRef } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { TMM_URL } from '../toolkit/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMatch,
  deleteMatch,
  updateMatch,
  setProducerStatus,
  fetchLiveMatches,
  fetchFullEvent,
  setLocales
} from 'stores/match';
import {
  getBetSlipMatches,
  checkEventBetStatus_UpdateMsg,
  checkEventBetStatus_DeleteMsg
} from 'stores/betSlip';
import {
  addOutrights,
  updateOutrights,
  deleteOutrights
} from 'stores/outrights';

const RETRY_INTERVALS = [1000, 3000, 5000, 7000, 10000, 15000, 20000];

const useMakeHubConnection = () => {
  const dispatch = useDispatch();
  const selectedMatch = useSelector((state) => state.match.selectedMatch);
  const retryIndexRef = useRef(0);

  const onReconnect = () => {
    console.log('onReconnect');
    dispatch(getBetSlipMatches());
    dispatch(fetchLiveMatches());
    if (selectedMatch) {
      dispatch(fetchFullEvent(selectedMatch));
    }
  };

  useEffect(() => {
    const conn = new HubConnectionBuilder()
      .configureLogging(LogLevel.None)
      .withUrl(`${TMM_URL}/hubs/sportEvent`, {
        headers: {
          'Access-Control-Allow-Headers': 'Content-Type',
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'OPTIONS,POST,GET',
          'Access-Control-Allow-Credentials': 'true'
        }
      })
      .build();

    let isRetrying = false;
    let retryTimeoutId = null;

    const restart = async () => {
      if (!conn) {
        return;
      }
      if (conn.state !== HubConnectionState.Disconnected) {
        return;
      }
      if (isRetrying) {
        return;
      }

      if (retryIndexRef.current >= RETRY_INTERVALS.length) {
        return;
      }

      isRetrying = true;

      try {
        await conn.start();
        console.log('Connected to SignalR');
        retryIndexRef.current = 0;
        isRetrying = false;
        onReconnect();
      } catch (err) {
        isRetrying = false;
        console.log(
          `CATCH ERROR HUB START - Retry attempt ${retryIndexRef.current + 1}/${
            RETRY_INTERVALS.length
          }`
        );
        const retryDelay = RETRY_INTERVALS[retryIndexRef.current];
        retryIndexRef.current += 1;
        retryTimeoutId = setTimeout(() => {
          restart();
        }, retryDelay);
      }
    };

    conn.onclose(() => {
      console.log('Disconnected. Restarting.');
      restart();
    });

    conn
      .start()
      .then(async () => {
        console.log('Connected to SignalR');
        retryIndexRef.current = 0;
      })
      .catch(() => {
        console.log('Error starting SignalR');
        restart();
      });

    conn.on('BetEventAdded', (data) => {
      dispatch(addMatch(data));
    });

    conn.on('BetEventUpdated', (data) => {
      dispatch(updateMatch(data));
      dispatch(checkEventBetStatus_UpdateMsg(data));
    });

    conn.on('BetEventDeleted', (data) => {
      dispatch(deleteMatch(data));
      dispatch(checkEventBetStatus_DeleteMsg(data));
    });

    conn.on('LocalizationAdded', (data) => {
      dispatch(setLocales(data));
    });

    conn.on('ProducerStatusChanged', (data) => {
      dispatch(setProducerStatus(data));
    });

    conn.on('OutrightsBetEventAdded', (data) => {
      dispatch(addOutrights(data));
    });

    conn.on('OutrightsBetEventUpdated', (data) => {
      dispatch(updateOutrights(data));
    });

    conn.on('OutrightsBetEventDeleted', (data) => {
      dispatch(deleteOutrights(data));
    });

    // Cleanup function to prevent memory leaks
    return () => {
      console.log('Cleaning up SignalR connection');
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
      if (conn) {
        conn
          .stop()
          .then(() => console.log('SignalR connection stopped'))
          .catch((err) => console.error('Error stopping SignalR connection:', err));
      }
    };
  }, []);
};

export { useMakeHubConnection };
